// Function index:
//   renderWorldRules(host) — fetch + render every world's GM-authored house
//       rules AND conditional codex rules, each grouped by world, with a
//       Revoke button on active rules. Revoked rules are shown
//       dimmed/struck-through (soft delete; never removed). Codex rules also
//       show a judgeBadge (async Haiku second-opinion verdict) and an
//       "amended ×N" chip.
//   judgeBadge(r) — render a codex rule's judge verdict as a `⚖` badge,
//       titled with the judge's reason; distinguishes judge-struck from an
//       admin revoke.

import {
  listAdminWorldRules, revokeAdminWorldRule, type AdminWorldRule,
  listAdminCodexRules, revokeAdminCodexRule, type AdminCodexRule,
} from '../../api.js';
import { esc } from './helpers.js';

export async function renderWorldRules(host: HTMLElement): Promise<void> {
  async function load(): Promise<void> {
    host.innerHTML = 'Loading…';
    try {
      const [{ rules }, { rules: codexRules }] = await Promise.all([
        listAdminWorldRules(), listAdminCodexRules(),
      ]);
      host.innerHTML = (rules.length ? renderHouseRules(rules) : '<div class="party-meta">no house rules yet</div>')
        + renderCodexRules(codexRules);
      wireRevoke(host, '.wr-revoke', revokeAdminWorldRule, load);
      wireRevoke(host, '.cr-revoke', revokeAdminCodexRule, load);
    } catch (err) {
      host.innerHTML = '';
      const e = document.createElement('div');
      e.className = 'party-meta';
      e.textContent = `Could not load world rules: ${(err as Error).message}`;
      host.appendChild(e);
    }
  }
  await load();
}

function wireRevoke(
  host: HTMLElement,
  selector: string,
  revoke: (id: number) => Promise<{ ok: boolean; revoked: boolean }>,
  reload: () => Promise<void>,
): void {
  host.querySelectorAll<HTMLButtonElement>(selector).forEach((btn) => {
    btn.addEventListener('click', async () => {
      btn.disabled = true;
      try { await revoke(Number(btn.dataset.id)); await reload(); }
      catch (err) { btn.disabled = false; alert(`revoke failed: ${(err as Error).message}`); }
    });
  });
}

function groupByWorld<T extends { world_id: number; world_name: string }>(rules: T[]): Map<string, T[]> {
  const byWorld = new Map<string, T[]>();
  for (const r of rules) {
    const key = `${r.world_id}:${r.world_name}`;
    (byWorld.get(key) ?? byWorld.set(key, []).get(key)!).push(r);
  }
  return byWorld;
}

function renderHouseRules(rules: AdminWorldRule[]): string {
  const blocks: string[] = [];
  for (const [key, group] of groupByWorld(rules)) {
    const worldName = key.slice(key.indexOf(':') + 1);
    const items = group.map((r) => {
      const revoked = r.status !== 'active';
      const style = revoked ? 'opacity:0.5; text-decoration:line-through;' : '';
      const btn = revoked ? '<span class="party-meta">revoked</span>'
        : `<button class="secondary wr-revoke" data-id="${r.id}">Revoke</button>`;
      const why = r.rationale ? ` <span class="party-meta">(${esc(r.rationale)})</span>` : '';
      return `<li style="${style} margin:4px 0; display:flex; gap:8px; align-items:center;">
        <span style="flex:1;">${esc(r.text)}${why}</span>${btn}</li>`;
    }).join('');
    blocks.push(`<div style="margin-bottom:14px;">
      <h3 style="margin:0 0 4px;">${esc(worldName)}</h3>
      <ul style="list-style:none; padding:0; margin:0;">${items}</ul></div>`);
  }
  return blocks.join('');
}

function judgeBadge(r: AdminCodexRule): string {
  const title = r.judge_reason ? ` title="${esc(r.judge_reason)}"` : '';
  if (r.status !== 'active' && r.judge_verdict === 'revoke' && r.revoked_by === null) {
    return `<span class="party-meta"${title}>⚖ struck by judge</span>`;
  }
  if (r.judge_verdict) {
    const style = r.judge_verdict === 'review' ? ' style="color:#c90;"' : '';
    return `<span class="party-meta"${title}${style}>⚖ ${esc(r.judge_verdict)}</span>`;
  }
  return '<span class="party-meta">⚖ unjudged</span>';
}

function renderCodexRules(rules: AdminCodexRule[]): string {
  const body = rules.length === 0
    ? '<div class="party-meta">no codex rules yet</div>'
    : [...groupByWorld(rules)].map(([key, group]) => {
      const worldName = key.slice(key.indexOf(':') + 1);
      const items = group.map((r) => {
        const revoked = r.status !== 'active';
        const style = revoked ? 'opacity:0.5; text-decoration:line-through;' : '';
        const btn = revoked ? '<span class="party-meta">revoked</span>'
          : `<button class="secondary cr-revoke" data-id="${r.id}">Revoke</button>`;
        const why = r.rationale ? ` <span class="party-meta">(${esc(r.rationale)})</span>` : '';
        const chips = [...r.contexts, ...r.keywords]
          .map((c) => `<span class="chip" style="font-size:0.85em; padding:1px 6px; border-radius:10px; background:rgba(128,128,128,0.2);">${esc(c)}</span>`)
          .join(' ');
        const judge = judgeBadge(r);
        const amended = r.amend_count > 0
          ? `<span class="chip" style="font-size:0.85em; padding:1px 6px; border-radius:10px; background:rgba(128,128,255,0.2);">amended ×${r.amend_count}</span>`
          : '';
        return `<li style="${style} margin:4px 0; display:flex; gap:8px; align-items:center;">
          <span style="flex:1;">${esc(r.text)}${why} ${chips} ${judge} ${amended}</span>${btn}</li>`;
      }).join('');
      return `<div style="margin-bottom:14px;">
        <h3 style="margin:0 0 4px;">${esc(worldName)}</h3>
        <ul style="list-style:none; padding:0; margin:0;">${items}</ul></div>`;
    }).join('');
  return `<div style="margin-top:18px;"><h3 style="margin:0 0 8px;">Codex rules (conditional)</h3>${body}</div>`;
}
